import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Paper,
  Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import VerifiedIcon from "@mui/icons-material/Verified";
import CurrencyPoundIcon from "@mui/icons-material/CurrencyPound";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CampaignIcon from "@mui/icons-material/Campaign";
import {
  CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import api from "../../api";
import AnalyticsPageShell, { CARD_SX } from "./AnalyticsPageShell";
import AnalyticsDataTable from "./AnalyticsDataTable";
import {
  PLATFORM_META, RANGE_PRESETS, channelColor, channelLabel, formatCount, formatGBP, formatPercent,
  funnelWithRates, isEmptyReport, leadSourceLabel, platformLabel, rangeToDates, sortRows,
} from "./marketingAnalyticsUtils";

const PURPLE = "#7c3aed";

const DEFAULT_FILTERS = {
  channels: ["facebook", "instagram", "tiktok", "youtube", "whatsapp", "google_ads", "google_organic", "email", "referral", "direct", "other", "unattributed"],
  platforms: ["web", "ios", "android", "unknown"],
};

function KpiCard({ label, value, hint, Icon, iconBg, iconColor }) {
  return (
    <Paper sx={{ ...CARD_SX, display: "flex", alignItems: "center", gap: 1.5, height: "100%" }}>
      <Box sx={{ width: 44, height: 44, borderRadius: "50%", bgcolor: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon sx={{ color: iconColor, fontSize: 22 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary" noWrap>{label}</Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{value}</Typography>
        {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
      </Box>
    </Paper>
  );
}

function SectionCard({ title, subtitle, children }) {
  return (
    <Paper sx={{ ...CARD_SX, height: "100%" }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
      {subtitle && <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>{subtitle}</Typography>}
      <Box sx={{ mt: subtitle ? 0 : 1 }}>{children}</Box>
    </Paper>
  );
}

function ChannelName({ channel }) {
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
      <Box component="span" sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: channelColor(channel), flexShrink: 0 }} />
      <span>{channelLabel(channel)}</span>
    </Box>
  );
}

function Funnel({ funnel }) {
  const steps = funnelWithRates(funnel);
  const max = Math.max(1, ...steps.map((s) => s.count));
  return (
    <Stack spacing={1.25} data-testid="marketing-funnel">
      {steps.map((s, i) => (
        <Box key={s.step}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.label}</Typography>
            <Typography variant="body2" color="text.secondary">
              {formatCount(s.count)}
              {s.stepRate !== null && ` · ${formatPercent(s.stepRate)} of previous`}
            </Typography>
          </Box>
          <Box sx={{ height: 14, borderRadius: 7, bgcolor: "#f1f5f9", overflow: "hidden" }}>
            <Box sx={{
              height: "100%",
              width: `${Math.max(s.count > 0 ? 3 : 0, (s.count / max) * 100)}%`,
              bgcolor: PURPLE,
              opacity: 1 - i * 0.18,
              borderRadius: 7,
            }} />
          </Box>
        </Box>
      ))}
    </Stack>
  );
}

const CHANNEL_COLUMNS = [
  { key: "channel", label: "Channel" },
  { key: "leads", label: "Leads" },
  { key: "signups", label: "Sign-ups" },
  { key: "trials_started", label: "Trials" },
  { key: "paid_conversions", label: "Paid" },
  { key: "revenue_gbp", label: "Revenue" },
  { key: "conversion_rate", label: "Sign-up → paid" },
];

function ChannelsTable({ rows }) {
  const [sort, setSort] = useState({ key: "signups", direction: "desc" });
  const sorted = useMemo(() => sortRows(rows, sort.key, sort.direction), [rows, sort]);
  const toggle = (key) => setSort((s) => ({ key, direction: s.key === key && s.direction === "desc" ? "asc" : "desc" }));

  if (!rows?.length) {
    return <Typography color="text.secondary" align="center" sx={{ py: 3 }}>No channel data for the selected filters.</Typography>;
  }

  const cell = (row, key) => {
    if (key === "channel") return <ChannelName channel={row.channel} />;
    if (key === "revenue_gbp") return formatGBP(row.revenue_gbp);
    if (key === "conversion_rate") return formatPercent(row.conversion_rate);
    return formatCount(row[key]);
  };

  return (
    <TableContainer>
      <Table size="small" aria-label="Channels">
        <TableHead>
          <TableRow>
            {CHANNEL_COLUMNS.map((c) => (
              <TableCell key={c.key} sx={{ fontWeight: 700 }} align={c.key === "channel" ? "left" : "right"} sortDirection={sort.key === c.key ? sort.direction : false}>
                <TableSortLabel active={sort.key === c.key} direction={sort.key === c.key ? sort.direction : "desc"} onClick={() => toggle(c.key)}>
                  {c.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((row) => (
            <TableRow key={row.channel} hover>
              {CHANNEL_COLUMNS.map((c) => (
                <TableCell key={c.key} align={c.key === "channel" ? "left" : "right"}>{cell(row, c.key)}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function PlatformSplit({ rows }) {
  const data = (rows || []).map((r) => ({ ...r, name: platformLabel(r.platform) }));
  const total = data.reduce((sum, r) => sum + (Number(r.signups) || 0), 0);
  return (
    <Box>
      {total > 0 && (
        <Box sx={{ height: 180 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data.filter((r) => r.signups > 0)} dataKey="signups" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                {data.filter((r) => r.signups > 0).map((r) => (
                  <Cell key={r.platform} fill={PLATFORM_META[r.platform]?.color || "#CBD5E1"} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Box>
      )}
      <Table size="small" aria-label="Platforms">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Platform</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">Sign-ups</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">Paid</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((r) => (
            <TableRow key={r.platform}>
              <TableCell>
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                  <Box component="span" sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: PLATFORM_META[r.platform]?.color || "#CBD5E1" }} />
                  {r.name}
                </Box>
              </TableCell>
              <TableCell align="right">{formatCount(r.signups)}</TableCell>
              <TableCell align="right">{formatCount(r.paid_conversions)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function TrendChart({ trend }) {
  return (
    <Box sx={{ height: 280 }} data-testid="marketing-trend">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={trend || []} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => String(d).slice(5)} minTickGap={16} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey="leads" name="Leads" stroke="#94A3B8" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="signups" name="Sign-ups" stroke={PURPLE} strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="trials_started" name="Trials" stroke="#F59E0B" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="paid_conversions" name="Paid" stroke="#16A34A" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}

export function MarketingFiltersBar({ preset, setPreset, custom, setCustom, channel, setChannel, platform, setPlatform, filters }) {
  const channels = filters?.channels?.length ? filters.channels : DEFAULT_FILTERS.channels;
  const platforms = filters?.platforms?.length ? filters.platforms : DEFAULT_FILTERS.platforms;
  return (
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={12} md="auto">
        <ToggleButtonGroup size="small" exclusive value={preset} onChange={(_, v) => v && setPreset(v)} aria-label="Date range">
          {RANGE_PRESETS.map((p) => (
            <ToggleButton key={p.value} value={p.value} sx={{ textTransform: "none", px: 1.5 }}>{p.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Grid>
      {preset === "custom" && (
        <>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
              value={custom.from} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
              value={custom.to} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} />
          </Grid>
        </>
      )}
      <Grid item xs={6} md={2}>
        <FormControl fullWidth size="small">
          <InputLabel id="mkt-channel">Channel</InputLabel>
          <Select labelId="mkt-channel" label="Channel" value={channel} onChange={(e) => setChannel(e.target.value)}>
            <MenuItem value="">All channels</MenuItem>
            {channels.map((c) => <MenuItem key={c} value={c}>{channelLabel(c)}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={6} md={2}>
        <FormControl fullWidth size="small">
          <InputLabel id="mkt-platform">Platform</InputLabel>
          <Select labelId="mkt-platform" label="Platform" value={platform} onChange={(e) => setPlatform(e.target.value)}>
            <MenuItem value="">All platforms</MenuItem>
            {platforms.map((p) => <MenuItem key={p} value={p}>{platformLabel(p)}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );
}

/** Presentational body: KPI cards, funnel, trend, tables. Exported for tests. */
export function MarketingReport({ data }) {
  const s = data?.summary || {};

  if (isEmptyReport(data)) {
    return (
      <Paper sx={{ ...CARD_SX, textAlign: "center", py: 6 }} data-testid="marketing-empty">
        <CampaignIcon sx={{ fontSize: 48, color: PURPLE, mb: 1 }} />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Marketing tracking has just started</Typography>
        <Typography color="text.secondary" sx={{ maxWidth: 560, mx: "auto", mt: 1 }}>
          There are no leads, sign-ups or payments for this period yet. Data will appear here as new
          visitors arrive and sign up — tag the links you share (for example
          {" "}<code>?utm_source=instagram&amp;utm_medium=social&amp;utm_campaign=free_mock</code>) so each
          channel is credited.
        </Typography>
      </Paper>
    );
  }

  const kpis = [
    { label: "Leads", value: formatCount(s.leads), Icon: ContactMailIcon, iconBg: "#f1f5f9", iconColor: "#475569" },
    { label: "Sign-ups", value: formatCount(s.signups), Icon: PersonAddAlt1Icon, iconBg: "#ede9fe", iconColor: PURPLE },
    { label: "Trials started", value: formatCount(s.trials_started), Icon: HourglassTopIcon, iconBg: "#fef3c7", iconColor: "#d97706" },
    { label: "Paid conversions", value: formatCount(s.paid_conversions), Icon: VerifiedIcon, iconBg: "#dcfce7", iconColor: "#16a34a" },
    { label: "Revenue", value: formatGBP(s.revenue_gbp), Icon: CurrencyPoundIcon, iconBg: "#dcfce7", iconColor: "#15803d" },
    { label: "Lead → sign-up", value: formatPercent(s.lead_to_signup_rate), Icon: TrendingUpIcon, iconBg: "#dbeafe", iconColor: "#2563eb" },
    { label: "Trial → paid", value: formatPercent(s.trial_to_paid_rate), Icon: TrendingUpIcon, iconBg: "#ffe4e6", iconColor: "#e11d48" },
  ];

  const campaignColumns = [
    { key: "utm_source", label: "Source", render: (r) => r.utm_source || "—" },
    { key: "utm_medium", label: "Medium", render: (r) => r.utm_medium || "—" },
    { key: "utm_campaign", label: "Campaign", render: (r) => r.utm_campaign || "—" },
    { key: "signups", label: "Sign-ups", render: (r) => formatCount(r.signups) },
    { key: "paid_conversions", label: "Paid", render: (r) => formatCount(r.paid_conversions) },
    { key: "revenue_gbp", label: "Revenue", render: (r) => formatGBP(r.revenue_gbp) },
  ];
  const leadSourceColumns = [
    { key: "source", label: "Source", render: (r) => leadSourceLabel(r.source) },
    { key: "count", label: "Leads", render: (r) => formatCount(r.count) },
    { key: "converted", label: "Converted", render: (r) => formatCount(r.converted) },
  ];
  const landingColumns = [
    { key: "landing_page", label: "Landing page", render: (r) => <code style={{ wordBreak: "break-all" }}>{r.landing_page}</code> },
    { key: "signups", label: "Sign-ups", render: (r) => formatCount(r.signups) },
  ];
  const withIds = (rows, keyFn) => (rows || []).map((r, i) => ({ ...r, id: keyFn ? keyFn(r, i) : i }));

  return (
    <>
      {Number(s.unattributed_signups) > 0 && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }} data-testid="marketing-unattributed">
          {formatCount(s.unattributed_signups)} of {formatCount(s.signups)} sign-ups in this period have no recorded
          source ("Unattributed"). They signed up before tracking started, or came from a client that does not send
          attribution yet (for example older app versions).
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {kpis.map((k) => (
          <Grid item xs={6} sm={4} md={3} lg key={k.label}>
            <KpiCard {...k} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <SectionCard title="Funnel" subtitle="Leads → sign-ups → trials → paid, with each step's % of the one before">
            <Funnel funnel={data.funnel} />
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={8}>
          <SectionCard title="Trend" subtitle="Daily leads, sign-ups, trials and paid conversions">
            <TrendChart trend={data.trend} />
          </SectionCard>
        </Grid>
      </Grid>

      <Box sx={{ mb: 2 }}>
        <SectionCard title="Channels" subtitle="Click a column to sort. Revenue is credited to the family's first-touch channel.">
          <ChannelsTable rows={data.by_channel} />
        </SectionCard>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Campaigns (UTM)</Typography>
          <AnalyticsDataTable
            columns={campaignColumns}
            rows={withIds(data.by_campaign, (r, i) => `${r.utm_source}|${r.utm_medium}|${r.utm_campaign}|${i}`)}
            loading={false}
            emptyMessage="No tagged campaigns yet. Add utm_source, utm_medium and utm_campaign to the links you share."
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SectionCard title="Platform split" subtitle="Where families signed up">
            <PlatformSplit rows={data.by_platform} />
          </SectionCard>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Lead sources</Typography>
          <AnalyticsDataTable columns={leadSourceColumns} rows={withIds(data.lead_sources, (r) => r.source)} loading={false} emptyMessage="No leads in this period." />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Top landing pages</Typography>
          <AnalyticsDataTable columns={landingColumns} rows={withIds(data.top_landing_pages, (r) => r.landing_page)} loading={false} emptyMessage="No landing pages recorded yet." />
        </Grid>
      </Grid>
    </>
  );
}

export default function MarketingAnalytics() {
  const [preset, setPreset] = useState("30d");
  const [custom, setCustom] = useState(() => rangeToDates("30d"));
  const [channel, setChannel] = useState("");
  const [platform, setPlatform] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const range = useMemo(() => rangeToDates(preset, custom), [preset, custom]);

  const load = useCallback(async () => {
    if (preset === "custom" && (!range.from || !range.to)) return;
    try {
      setLoading(true);
      setError("");
      const params = { from: range.from, to: range.to };
      if (channel) params.channel = channel;
      if (platform) params.platform = platform;
      const res = await api.get("admin/analytics/marketing", { params });
      setData(res.data?.data || res.data || null);
    } catch (e) {
      setError(e.response?.data?.message || "Failed to load marketing analytics.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [preset, range.from, range.to, channel, platform]);

  useEffect(() => { load(); }, [load]);

  return (
    <AnalyticsPageShell
      title="Marketing"
      subtitle="Which channels bring leads, sign-ups, trials and paying customers. Attribution is first-touch: the first tracked visit of each family."
      filters={
        <MarketingFiltersBar
          preset={preset}
          setPreset={setPreset}
          custom={custom}
          setCustom={setCustom}
          channel={channel}
          setChannel={setChannel}
          platform={platform}
          setPlatform={setPlatform}
          filters={data?.filters}
        />
      }
    >
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      ) : (
        <MarketingReport data={data} />
      )}
    </AnalyticsPageShell>
  );
}
